
import { ActionItem } from "../../types/actionItems";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { formatDate } from "../../utils/date";
import { useUserTimezone } from "../../hooks/useUserTimezone";
import { StatusBadge } from "../shared/StatusBadge";
import { RichTextContent } from "../shared/RichTextContent";
import React from "react";

interface ActionItemCardProps {
  item: ActionItem & { meetings?: { title: string; } | null };
  onClick?: () => void;
  meetingTitle?: string;
}

export function ActionItemCard({ item, onClick, meetingTitle }: ActionItemCardProps) {
  const { timezone } = useUserTimezone();

  const isOverdue = (dueDate: string | null) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  // Support both old and new formats
  const renderObjective = () => {
    if (!item.description) return null;
    
    // Type guard to check if it's a rich text object
    const isRichTextObject = (obj: any): obj is { format: string; text: string } => {
      return typeof obj === 'object' && obj !== null && 'format' in obj;
    };
    
    if (isRichTextObject(item.description)) {
      return <RichTextContent content={item.description} />;
    }
    
    // If it's a plain string
    return (
      <p className="text-sm text-muted-foreground font-medium leading-tight">
        {item.description}
      </p>
    );
  };

  return (
    <Card 
      className="hover:bg-muted/50 transition-shadow cursor-pointer bg-background"
      onClick={onClick}
    >
      <CardContent className="p-4 sm:p-6 space-y-2">
        <div className="flex justify-between items-start gap-4">
          <h3 className="text-lg font-medium text-primary leading-tight">
            {item.title}
          </h3>
          <StatusBadge status={item.status} className="shrink-0" />
        </div>
        
        {renderObjective()}
        
        <div className="flex flex-wrap gap-2">
          {meetingTitle && (
            <Badge 
              variant="outline" 
              className="px-2.5 py-0.5 bg-primary/10 text-primary border-none rounded-[10px] text-[10px] font-medium leading-none whitespace-nowrap"
            >
              {meetingTitle}
            </Badge>
          )}
          {'priority' in item && item.priority && (
            <RichTextContent 
              content={{ 
                text: item.priority, 
                level: item.priority, 
                format: 'badge' 
              }} 
            />
          )}
        </div>
        
        <div className="flex flex-col sm:flex-row justify-between gap-2 sm:gap-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-muted-foreground font-medium leading-tight">Created:</span>
            <span className="text-sm text-muted-foreground font-light leading-tight">
              {formatDate(item.created_at, timezone)}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-muted-foreground font-medium leading-tight">Due:</span>
            <span className={`text-sm font-light leading-tight ${
              isOverdue(item.due_date) 
                ? 'text-destructive dark:text-red-400 font-bold' 
                : 'text-muted-foreground'
            }`}>
              {item.due_date ? formatDate(item.due_date, timezone) : 'No due date'}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
